/**
 * ENG-8378 slice 2 (CS-1540): the artefacts a completed kanban card produced.
 *
 * `deliverable` (intent, singular, set at creation, FTS-indexed) and `artefacts`
 * (outcome, plural, set at completion) sit side by side deliberately. This
 * module owns the second: the closed kind vocabulary, the URL validator, and the
 * normaliser every write path runs input through.
 *
 * WHY THE KIND IS ASSERTED BY THE AGENT AND NOT SNIFFED FROM THE URL.
 *
 * The agent just made the thing, so it is the cheapest reliable source, and a
 * closed enum is validatable where free text is not. Sniffing is offered only as
 * a fallback (`deriveArtefactKind`) because it genuinely lies: shorteners,
 * redirectors, extensionless API endpoints and presigned S3 URLs (extension in
 * the query string, or absent) all defeat it. What is NOT offered, at any tier,
 * is fetching the URL to read its Content-Type — that would be a brand new
 * outbound-fetch-of-agent-supplied-URL surface in a tree that has none, i.e. an
 * SSRF hole opened to improve an icon.
 *
 * WHAT `validateArtefactUrl` IS AND IS NOT.
 *
 * It is NOT an SSRF control, because nothing here ever dereferences the URL. It
 * is two narrower things:
 *
 *   1. An XSS guard. These values are rendered as `href`s on the webapp card and
 *      in three chat surfaces. `javascript:` and `data:` in an href are script
 *      execution, so the scheme allowlist is load-bearing rather than tidy.
 *   2. A usefulness guard. An artefact URL exists to be opened BY A HUMAN, on a
 *      different machine. `file:///root/.augmented/…`, `localhost:3000` and
 *      `10.x` all point at the agent's own box and are dead on arrival for the
 *      reader — and they are a live confusion, not a hypothetical: agents
 *      routinely cite on-disk `~/.augmented/{codeName}/…` paths, and
 *      skills/kanban/SKILL.md's own example mixes an https URL and a local path
 *      in one `result`.
 *
 * Because the private-range rules are about usefulness rather than defence, they
 * are deliberately literal: this rejects the hostnames a confused agent actually
 * writes. It is not trying to beat an adversary armed with decimal-encoded IPs,
 * and it should not be mistaken for something that does.
 */

/**
 * What an artefact IS, from the closed set an agent may assert.
 *
 * Deliberately short. A vocabulary that grows synonyms ('artifact', 'report',
 * 'summary') is one no renderer can branch on, which is how the `result_kind`
 * migration describes a badge drifting back to meaning nothing. Anything that
 * does not fit is `other`, which renders honestly rather than wrongly.
 */
export type ArtefactKind =
  /** An Augmented Live page — the platform's own published-artefact surface. */
  | 'live'
  /** A console dashboard. */
  | 'dashboard'
  /** A doc, page, brief, or writeup. */
  | 'document'
  /** A sheet, CSV, or tabular export. */
  | 'spreadsheet'
  | 'image'
  | 'video'
  | 'audio'
  /** A skill definition in the shared registry. */
  | 'skill'
  /** A scheduled task / routine the work created. */
  | 'scheduled_task'
  /** A knowledge-base entry. */
  | 'knowledge'
  | 'pull_request'
  /** A ticket in Linear, GitHub, Jira, etc. */
  | 'issue'
  /** Real, and not a failure to choose — see the type docblock. */
  | 'other';

/**
 * The canonical kinds as a runtime array. Paired with the type above the way
 * `WAITING_KINDS` is paired with `WaitingKind` in waiting.ts, so the guard and
 * any UI enumeration read from one source.
 *
 * Typed as a readonly TUPLE of literals rather than `readonly ArtefactKind[]`,
 * because the stdio MCP tool feeds it straight to `z.enum()`, which needs the
 * literal members to build its schema. A widened element type would compile here
 * and fail at the one call site that matters.
 */
export const ARTEFACT_KINDS = [
  'live',
  'dashboard',
  'document',
  'spreadsheet',
  'image',
  'video',
  'audio',
  'skill',
  'scheduled_task',
  'knowledge',
  'pull_request',
  'issue',
  'other',
] as const satisfies readonly ArtefactKind[];

/**
 * Compile-time exhaustiveness: adding a kind to the union without listing it
 * above is a type error here, not a runtime surprise in the enum a tool
 * advertises. `satisfies` only checks the converse (nothing extra in the array).
 * `describeArtefactKind`'s switch covers the third direction.
 */
type AssertTrue<T extends true> = T;
type _EveryKindIsListed = AssertTrue<
  Exclude<ArtefactKind, (typeof ARTEFACT_KINDS)[number]> extends never ? true : never
>;

const ARTEFACT_KIND_SET: ReadonlySet<string> = new Set<string>(ARTEFACT_KINDS);

/** Narrowing guard for an arbitrary value against the canonical kind set. */
export function isArtefactKind(value: unknown): value is ArtefactKind {
  return typeof value === 'string' && ARTEFACT_KIND_SET.has(value);
}

/** One artefact a completed card produced. */
export interface KanbanArtefact {
  kind: ArtefactKind;
  url: string;
  /** Human label. Falls back to the kind's label when the agent omits it. */
  label?: string;
}

/**
 * Cap per card. Five is the point past which a chip row stops being scannable
 * and starts being a list, on the narrowest surface that renders it.
 */
export const MAX_ARTEFACTS = 5;

/**
 * URL length cap. 2048 is the conventional practical ceiling; the point here is
 * only that an unbounded agent string cannot bloat every row that carries one.
 */
export const MAX_ARTEFACT_URL = 2048;

/** Label length cap — a chip label, not a description. */
export const MAX_ARTEFACT_LABEL = 80;

/**
 * Icon + human label for a kind.
 *
 * The emoji NEVER ships alone. A bare glyph conveys nothing to a screen reader
 * and is not distinguishable at chip size, so every renderer pairs it with this
 * `label`; `direct-chat-attachment.tsx` (icon + filename + size) is the in-repo
 * precedent. For the same reason "this card produced an artefact" must be
 * signalled by text or shape, never by colour alone: colour-only fails
 * colour-blind readers and is invisible on all three chat surfaces, which is
 * where the customer said first contact happens.
 */
export function describeArtefactKind(kind: ArtefactKind): { emoji: string; label: string } {
  switch (kind) {
    case 'live':
      return { emoji: '🌐', label: 'Live page' };
    case 'dashboard':
      return { emoji: '📊', label: 'Dashboard' };
    case 'document':
      return { emoji: '📄', label: 'Document' };
    case 'spreadsheet':
      return { emoji: '📈', label: 'Spreadsheet' };
    case 'image':
      return { emoji: '🖼️', label: 'Image' };
    case 'video':
      return { emoji: '🎬', label: 'Video' };
    case 'audio':
      return { emoji: '🔊', label: 'Audio' };
    case 'skill':
      return { emoji: '🧩', label: 'Skill' };
    case 'scheduled_task':
      return { emoji: '⏰', label: 'Scheduled task' };
    case 'knowledge':
      return { emoji: '📚', label: 'Knowledge entry' };
    case 'pull_request':
      return { emoji: '🔀', label: 'Pull request' };
    case 'issue':
      return { emoji: '🎫', label: 'Issue' };
    case 'other':
      return { emoji: '🔗', label: 'Link' };
  }
}

/**
 * Why a URL was refused. Returned rather than thrown so a caller can report the
 * specific reason to the agent — "rejected with a clear error rather than
 * stored" is the acceptance criterion, and "invalid url" does not meet it.
 */
export type ArtefactUrlRejection =
  | 'not-a-string'
  | 'empty'
  | 'too-long'
  | 'unparseable'
  | 'bad-scheme'
  | 'no-host'
  | 'local-host';

export type ArtefactUrlResult =
  | { ok: true; url: string }
  | { ok: false; reason: ArtefactUrlRejection; message: string };

/**
 * Hostnames that resolve to the machine the agent runs on, or to a network only
 * it can see. Matched literally, and only against the hostname — see the module
 * docblock on why this is a usefulness rule, not a security boundary.
 */
const LOCAL_HOSTNAMES: ReadonlySet<string> = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '::1',
  '[::1]',
]);

/** Suffixes that only mean something inside one network. */
const LOCAL_SUFFIXES: readonly string[] = ['.localhost', '.local', '.internal', '.localdomain'];

function isPrivateIPv4(host: string): boolean {
  const parts = host.split('.');
  if (parts.length !== 4) return false;
  const nums = parts.map((p) => (/^\d{1,3}$/.test(p) ? Number(p) : NaN));
  if (nums.some((n) => Number.isNaN(n) || n > 255)) return false;
  const [a, b] = nums as [number, number, number, number];
  if (a === 10 || a === 127 || a === 0) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  // 169.254/16 — link-local, and the cloud metadata endpoint lives at
  // 169.254.169.254. Useless to a reader either way.
  if (a === 169 && b === 254) return true;
  return false;
}

function isLocalHostname(rawHost: string): boolean {
  // URL.hostname lowercases already; normalise anyway so a hand-built caller
  // cannot slip 'LOCALHOST' past this.
  const host = rawHost.toLowerCase();
  if (LOCAL_HOSTNAMES.has(host)) return true;
  if (LOCAL_SUFFIXES.some((suffix) => host.endsWith(suffix))) return true;
  if (isPrivateIPv4(host)) return true;

  // IPv6 ONLY, and "only" is load-bearing. `URL.hostname` returns an IPv6
  // literal wrapped in brackets and a DNS name bare, so the brackets are what
  // distinguishes the two — and the fc00::/7 test is a two-character prefix
  // match that a great many real domains begin with. Falling back to the
  // unbracketed host here (the first version of this did) rejected `fda.gov`,
  // `fdic.gov` and `fcc.gov` as machine-local, and because the write path
  // refuses the whole call on a bad URL, that would have failed the agent's
  // entire kanban_done over a link to a federal agency.
  //
  // Non-bracketed input has already been fully handled above.
  if (!host.startsWith('[') || !host.endsWith(']')) return false;
  const v6 = host.slice(1, -1);
  // Unique-local (fc00::/7) and link-local (fe80::/10), plus IPv4-mapped forms
  // of the private ranges above.
  if (v6.startsWith('fc') || v6.startsWith('fd') || v6.startsWith('fe80:')) return true;
  if (v6.startsWith('::ffff:') && isPrivateIPv4(ipv4FromMapped(v6.slice('::ffff:'.length)))) {
    return true;
  }
  return false;
}

/**
 * The IPv4 inside an IPv4-mapped IPv6 address, as dotted quad.
 *
 * Both spellings have to be handled, and the second one is easy to miss: the WHATWG
 * URL parser REWRITES the readable form into hex, so `http://[::ffff:10.0.0.1]/`
 * arrives here as `[::ffff:a00:1]`. A check that only understood the dotted form
 * would look correct, read correctly, and never fire — which is what the test for
 * this caught. Returns '' when the tail is neither shape, so the caller's
 * `isPrivateIPv4` simply says no.
 */
function ipv4FromMapped(tail: string): string {
  if (tail.includes('.')) return tail;
  const groups = tail.split(':');
  if (groups.length !== 2 || !groups.every((g) => /^[0-9a-f]{1,4}$/.test(g))) return '';
  const hi = Number.parseInt(groups[0] as string, 16);
  const lo = Number.parseInt(groups[1] as string, 16);
  return `${(hi >> 8) & 0xff}.${hi & 0xff}.${(lo >> 8) & 0xff}.${lo & 0xff}`;
}

/**
 * Validate one agent-supplied artefact URL.
 *
 * Nothing in this tree validated an agent-supplied URL before this: `source_url`
 * is rendered as a link with no check at all. One shared function so that stays
 * true of exactly one place rather than becoming true of four.
 */
export function validateArtefactUrl(raw: unknown): ArtefactUrlResult {
  if (typeof raw !== 'string') {
    return { ok: false, reason: 'not-a-string', message: 'url must be a string' };
  }
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: false, reason: 'empty', message: 'url must not be empty' };
  }
  if (trimmed.length > MAX_ARTEFACT_URL) {
    return {
      ok: false,
      reason: 'too-long',
      message: `url must be ${MAX_ARTEFACT_URL} characters or fewer`,
    };
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return {
      ok: false,
      reason: 'unparseable',
      message: 'url must be an absolute http(s) URL, e.g. https://example.com/report',
    };
  }

  // Allowlist, not a denylist. A denylist of `javascript:`/`data:`/`file:` is one
  // scheme behind whatever comes next, and the set of schemes a human can
  // usefully open from a card is exactly two.
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return {
      ok: false,
      reason: 'bad-scheme',
      message: `url scheme '${parsed.protocol.replace(/:$/, '')}' is not allowed — use http or https`,
    };
  }
  if (!parsed.hostname) {
    return { ok: false, reason: 'no-host', message: 'url must have a host' };
  }
  if (isLocalHostname(parsed.hostname)) {
    return {
      ok: false,
      reason: 'local-host',
      message:
        `url host '${parsed.hostname}' is local to this machine, so nobody else can open it — ` +
        'publish the artefact and link the published URL',
    };
  }

  // Return the PARSED serialisation, not the input. It is the normalised form
  // (scheme lowercased, host punycoded, spaces encoded), so two agents writing
  // the same link store the same string.
  return { ok: true, url: parsed.toString() };
}

/**
 * Best-effort kind from a URL, for when the agent did not assert one.
 *
 * Deliberately conservative: it recognises this platform's own surfaces and a
 * couple of unmistakable public ones, and answers `other` for everything else.
 * A wrong icon is worse than a generic one, because a wrong one is believed.
 */
export function deriveArtefactKind(url: string): ArtefactKind {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return 'other';
  }
  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname.toLowerCase();

  if (host.endsWith('github.com')) {
    if (/\/pull\/\d+/.test(path)) return 'pull_request';
    if (/\/issues\/\d+/.test(path)) return 'issue';
    return 'other';
  }
  if (host.endsWith('linear.app')) return 'issue';
  if (host.endsWith('augmented.team')) {
    if (path.startsWith('/live/')) return 'live';
    if (path.includes('/dashboard')) return 'dashboard';
    return 'other';
  }

  // Extension sniffing, last and narrowest. Only the unambiguous media
  // extensions, and only when the PATH ends in one — a query-string extension
  // (presigned S3) says nothing about the body.
  const ext = /\.([a-z0-9]{1,5})$/.exec(path)?.[1];
  switch (ext) {
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
    case 'svg':
      return 'image';
    case 'mp4':
    case 'mov':
    case 'webm':
      return 'video';
    case 'mp3':
    case 'wav':
    case 'm4a':
    case 'ogg':
      return 'audio';
    case 'csv':
    case 'tsv':
    case 'xlsx':
      return 'spreadsheet';
    case 'pdf':
    case 'md':
    case 'doc':
    case 'docx':
      return 'document';
    default:
      return 'other';
  }
}

/** One input entry that did not survive normalisation, and why. */
export interface RejectedArtefact {
  /** 1-based position in the caller's input array, for a legible error. */
  index: number;
  message: string;
}

export interface NormalizedArtefacts {
  artefacts: KanbanArtefact[];
  rejected: RejectedArtefact[];
  /** True when the input carried more than MAX_ARTEFACTS usable entries. */
  truncated: boolean;
}

/**
 * Normalise an arbitrary `artefacts` value into storable rows.
 *
 * Returns rejections rather than throwing, and separately from the survivors, so
 * a caller can decide the policy: the write paths refuse the whole call on any
 * rejection (AC2 — "rejected with a clear error rather than stored"), which is
 * right for a field an agent sets deliberately and would otherwise never learn
 * was dropped.
 *
 * Duplicate URLs collapse. An agent that lists the same report twice meant it
 * once, and two identical chips read as a rendering bug.
 */
export function normalizeArtefacts(raw: unknown): NormalizedArtefacts {
  const artefacts: KanbanArtefact[] = [];
  const rejected: RejectedArtefact[] = [];
  let truncated = false;

  if (raw === undefined || raw === null) return { artefacts, rejected, truncated };
  if (!Array.isArray(raw)) {
    return {
      artefacts,
      rejected: [{ index: 0, message: 'artefacts must be an array' }],
      truncated,
    };
  }

  const seen = new Set<string>();
  for (const [i, entry] of raw.entries()) {
    const index = i + 1;
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
      rejected.push({ index, message: 'each artefact must be an object with a url' });
      continue;
    }
    const item = entry as Record<string, unknown>;

    const urlResult = validateArtefactUrl(item.url);
    if (!urlResult.ok) {
      rejected.push({ index, message: urlResult.message });
      continue;
    }

    // Kind: asserted if valid, derived if absent, refused if present-and-wrong.
    // The third case is deliberate — silently correcting a typo'd kind would
    // teach the agent that the enum does not matter.
    let kind: ArtefactKind;
    if (item.kind === undefined || item.kind === null || item.kind === '') {
      kind = deriveArtefactKind(urlResult.url);
    } else if (isArtefactKind(item.kind)) {
      kind = item.kind;
    } else {
      rejected.push({
        index,
        message: `kind must be one of: ${ARTEFACT_KINDS.join(', ')}`,
      });
      continue;
    }

    let label: string | undefined;
    if (typeof item.label === 'string') {
      const trimmed = item.label.trim().slice(0, MAX_ARTEFACT_LABEL);
      if (trimmed.length > 0) label = trimmed;
    }

    if (seen.has(urlResult.url)) continue;
    seen.add(urlResult.url);

    if (artefacts.length >= MAX_ARTEFACTS) {
      truncated = true;
      continue;
    }
    artefacts.push(label === undefined ? { kind, url: urlResult.url } : { kind, url: urlResult.url, label });
  }

  return { artefacts, rejected, truncated };
}

/**
 * ENG-9248: what to DRAW on a chip, in three tiers.
 *
 * Brad, with a reference screenshot: *"I like how beautifului.dev shows them
 * with logos of the application that made them, or a placeholder for generic
 * formats like PDF"*.
 *
 * WHY THIS IS A NEW LAYER RATHER THAN A CHANGE TO `ArtefactKind`.
 *
 * `ArtefactKind` is a SEMANTIC category — what the thing IS. It is asserted by
 * the agent, closed, and validated. Nothing in that vocabulary can tell you the
 * artefact came from Figma rather than Notion, or that it is a PDF rather than a
 * Word doc, and widening it to carry provenance would break the one property
 * that makes it useful: that a renderer can branch on it exhaustively. So
 * provenance is DERIVED, sits beside the kind, and falls back to it.
 *
 * The tiers, in order:
 *
 *   1. `provider` — a known source application, from the URL HOSTNAME.
 *   2. `format`   — a generic file format, from the URL PATH extension.
 *   3. `kind`     — today's emoji, unchanged, when neither resolves.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. It never fetches the URL. The module
 * docblock above already rules that out for kind-sniffing and the reason is
 * identical here: reading a Content-Type would open an outbound-fetch-of-
 * agent-supplied-URL surface in a tree that has none — an SSRF hole opened to
 * improve an icon. An extensionless endpoint, a shortener or a presigned S3 URL
 * simply degrades to tier 3, which is the honest answer.
 *
 * ACCESSIBILITY CONTRACT, INHERITED AND NON-NEGOTIABLE. Every tier carries TEXT,
 * never colour alone: the provider and format tiers render a short abbreviation
 * INSIDE the badge, which is what makes the tone safe for colour-blind readers
 * and on surfaces that drop colour entirely. `describeArtefactKind`'s docblock
 * states the same rule for the emoji tier. A tone is decoration on top of an
 * abbreviation; it is never the signal.
 */

/**
 * Semantic colour family for a badge.
 *
 * Deliberately NOT a brand colour and NOT a raw palette value. The webapp's
 * `--color-{danger,success,info,warning}-*` families are theme-aware (they are
 * redefined under dark), and `check-raw-palette-classes.sh` (ENG-8857) fails CI
 * on a new raw Tailwind class. Naming a TONE here rather than a hex keeps the
 * decision in the design system where a dark-mode fix is one edit instead of
 * thirteen.
 */
export type ArtefactTone = 'danger' | 'success' | 'info' | 'warning' | 'neutral';

/** A source application recognised by hostname. */
export type ArtefactProvider =
  | 'augmented'
  | 'github'
  | 'linear'
  | 'figma'
  | 'notion'
  | 'google-docs'
  | 'google-sheets'
  | 'google-slides'
  | 'google-drive'
  | 'slack'
  | 'youtube';

/**
 * Hostname suffix → provider.
 *
 * Matched on a SUFFIX with a leading-dot or exact-equality test (see
 * `matchesHost`), never `includes`. `host.includes('figma.com')` would match
 * `figma.com.evil.example`, which is the classic way a host check becomes a
 * spoofing surface. Ordered longest-first so `docs.google.com` is tested before
 * anything that might match `google.com`.
 */
const PROVIDER_HOSTS: readonly (readonly [string, ArtefactProvider])[] = [
  ['docs.google.com', 'google-docs'],
  ['sheets.google.com', 'google-sheets'],
  ['slides.google.com', 'google-slides'],
  ['drive.google.com', 'google-drive'],
  ['augmented.team', 'augmented'],
  ['github.com', 'github'],
  ['linear.app', 'linear'],
  ['figma.com', 'figma'],
  ['notion.so', 'notion'],
  ['notion.site', 'notion'],
  ['slack.com', 'slack'],
  ['youtube.com', 'youtube'],
  ['youtu.be', 'youtube'],
];

/** Exact host, or a dot-boundary subdomain of it. Never a bare substring. */
function matchesHost(host: string, suffix: string): boolean {
  return host === suffix || host.endsWith(`.${suffix}`);
}

/**
 * Badge presentation for a provider.
 *
 * ABBREVIATION, NOT THE VENDOR'S LOGO — and that is a decision, not a shortcut.
 * Shipping a third party's brand mark into our console is a trademark question
 * that an engineer should not self-approve, and reproducing a mark from memory
 * risks shipping a subtly WRONG version of someone's logo, which is worse than
 * shipping none. A two-letter monogram in a neutral-to-tonal badge gives the
 * same at-a-glance provenance signal with none of that, and this function is the
 * single seam where real marks drop in later if Brad wants them: swap the return
 * to carry an SVG component id and only the renderer changes.
 */
export function describeArtefactProvider(provider: ArtefactProvider): {
  abbr: string;
  label: string;
  tone: ArtefactTone;
} {
  switch (provider) {
    case 'augmented':
      return { abbr: 'AT', label: 'Augmented', tone: 'success' };
    case 'github':
      return { abbr: 'GH', label: 'GitHub', tone: 'neutral' };
    case 'linear':
      return { abbr: 'LN', label: 'Linear', tone: 'info' };
    case 'figma':
      return { abbr: 'FG', label: 'Figma', tone: 'danger' };
    case 'notion':
      return { abbr: 'NO', label: 'Notion', tone: 'neutral' };
    case 'google-docs':
      return { abbr: 'GD', label: 'Google Docs', tone: 'info' };
    case 'google-sheets':
      return { abbr: 'GS', label: 'Google Sheets', tone: 'success' };
    case 'google-slides':
      return { abbr: 'GP', label: 'Google Slides', tone: 'warning' };
    case 'google-drive':
      return { abbr: 'DR', label: 'Google Drive', tone: 'warning' };
    case 'slack':
      return { abbr: 'SL', label: 'Slack', tone: 'danger' };
    case 'youtube':
      return { abbr: 'YT', label: 'YouTube', tone: 'danger' };
  }
}

/** The source application behind a URL, or null when the host is unknown. */
export function deriveArtefactProvider(url: string): ArtefactProvider | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  for (const [suffix, provider] of PROVIDER_HOSTS) {
    if (matchesHost(host, suffix)) return provider;
  }
  return null;
}

/**
 * Path extension → badge abbreviation and tone.
 *
 * Only formats a reader recognises on sight. An unlisted extension resolves to
 * null and the chip falls through to the kind emoji, which is the same
 * conservatism `deriveArtefactKind` applies: a wrong badge is worse than a
 * generic one, because a wrong one is believed.
 */
const FORMAT_BADGES: Readonly<Record<string, { abbr: string; tone: ArtefactTone }>> = {
  pdf: { abbr: 'PDF', tone: 'danger' },
  csv: { abbr: 'CSV', tone: 'success' },
  tsv: { abbr: 'TSV', tone: 'success' },
  xlsx: { abbr: 'XLS', tone: 'success' },
  xls: { abbr: 'XLS', tone: 'success' },
  doc: { abbr: 'DOC', tone: 'info' },
  docx: { abbr: 'DOC', tone: 'info' },
  md: { abbr: 'MD', tone: 'info' },
  txt: { abbr: 'TXT', tone: 'neutral' },
  json: { abbr: 'JSON', tone: 'neutral' },
  zip: { abbr: 'ZIP', tone: 'warning' },
  png: { abbr: 'PNG', tone: 'info' },
  jpg: { abbr: 'JPG', tone: 'info' },
  jpeg: { abbr: 'JPG', tone: 'info' },
  gif: { abbr: 'GIF', tone: 'info' },
  webp: { abbr: 'WEBP', tone: 'info' },
  svg: { abbr: 'SVG', tone: 'info' },
  mp4: { abbr: 'MP4', tone: 'warning' },
  mov: { abbr: 'MOV', tone: 'warning' },
  webm: { abbr: 'WEBM', tone: 'warning' },
  mp3: { abbr: 'MP3', tone: 'warning' },
  wav: { abbr: 'WAV', tone: 'warning' },
};

/**
 * Generic file format from the URL, or null.
 *
 * Reads the PATH only. A query-string extension (`?file=report.pdf`, and every
 * presigned S3 URL) says nothing about the body, and the existing
 * `deriveArtefactKind` makes the same call for the same reason.
 */
export function deriveArtefactFormat(url: string): { abbr: string; tone: ArtefactTone } | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const ext = /\.([a-z0-9]{1,5})$/.exec(parsed.pathname.toLowerCase())?.[1];
  if (ext === undefined) return null;
  return FORMAT_BADGES[ext] ?? null;
}

/**
 * What a chip should draw for one artefact: the resolved tier.
 *
 * A discriminated union rather than a bag of optionals, so the renderer branches
 * exhaustively and cannot accidentally draw two badges — the failure mode of
 * `{ provider?, format?, emoji }` is a chip with a logo AND a PDF square, which
 * is exactly the noise the tiering exists to prevent.
 */
export type ArtefactBadge =
  | { tier: 'provider'; abbr: string; label: string; tone: ArtefactTone }
  | { tier: 'format'; abbr: string; label: string; tone: ArtefactTone }
  | { tier: 'kind'; emoji: string; label: string };

/**
 * Resolve the badge for one artefact: provider, then format, then kind.
 *
 * `kind` is the caller's already-narrowed `ArtefactKind`, so this never has to
 * repeat the narrowing and the tier-3 fallback is guaranteed to exist.
 */
export function describeArtefactBadge(url: string, kind: ArtefactKind): ArtefactBadge {
  const provider = deriveArtefactProvider(url);
  if (provider !== null) {
    const described = describeArtefactProvider(provider);
    return { tier: 'provider', abbr: described.abbr, label: described.label, tone: described.tone };
  }
  const format = deriveArtefactFormat(url);
  if (format !== null) {
    return { tier: 'format', abbr: format.abbr, label: format.abbr, tone: format.tone };
  }
  const described = describeArtefactKind(kind);
  return { tier: 'kind', emoji: described.emoji, label: described.label };
}

// ──────────────────────────────────────────────────────────────────────
// ENG-9319 — the provider tier gets the REAL vendor mark, everywhere it can
// ──────────────────────────────────────────────────────────────────────

/**
 * The providers as a runtime tuple. Paired with `ArtefactProvider` exactly the
 * way `ARTEFACT_KINDS` is paired with `ArtefactKind`, and for the same reason:
 * a compile-time union cannot be iterated, so every runtime enumeration of the
 * providers was previously a hand-copied literal that nothing forced to stay in
 * sync.
 *
 * It exists because ENG-9319 adds a SECOND per-provider table
 * (`ARTEFACT_PROVIDER_CATALOG_KEYS`). `Record<ArtefactProvider, …>` makes a
 * missing entry a type error, but vitest transpiles without type-checking, so a
 * type error alone is not a failing test. This tuple is what lets the test walk
 * every member and prove the table covers it.
 */
export const ARTEFACT_PROVIDERS = [
  'augmented',
  'github',
  'linear',
  'figma',
  'notion',
  'google-docs',
  'google-sheets',
  'google-slides',
  'google-drive',
  'slack',
  'youtube',
] as const satisfies readonly ArtefactProvider[];

/** Compile-time converse of the `satisfies` above: nothing may be missing. */
type _EveryProviderIsListed = AssertTrue<
  Exclude<ArtefactProvider, (typeof ARTEFACT_PROVIDERS)[number]> extends never ? true : never
>;

/**
 * Provider → the catalogue row that carries its logo, or null when we ship none.
 *
 * THE VALUE IS A CATALOGUE KEY, NEVER A URL, and that is the whole point. The
 * logo urls live in `integration_definitions.icon` (keyed by `code_name`), they
 * are migrating to a first-party mirror under ENG-8756, and
 * `scripts/check-catalog-logo-hosts.mjs` ratchets the set of hosts that may
 * appear there. A url hardcoded in TypeScript is invisible to that ratchet, so
 * it would rot exactly the way the three already-dead vendor logos did — with
 * `IntegrationAvatar` swallowing the load error and rendering an initials tile,
 * which looks identical to "this integration never had a logo". Naming the row
 * instead means the mirror migration reaches these chips for free.
 *
 * A `null` is a POSITIVE statement — "the catalogue has no row for this
 * provider" — not an oversight. Verified against
 * `packages/supabase/seeds/integration-definitions.json`: there is no
 * `slack`, no `google-drive` and no `augmented` `code_name`. Those three render
 * the monogram, which is the designed fallback rather than a gap.
 *
 * Note the keys are the CATALOGUE's spelling, not ours: the provider is
 * `google-docs` and so is the `code_name`, but the two agreeing is a
 * coincidence worth not relying on — the icon behind it is
 * `…/api/googledocs`.
 */
export const ARTEFACT_PROVIDER_CATALOG_KEYS: Record<ArtefactProvider, string | null> = {
  // Our own product. No catalogue row for ourselves — we are not an
  // integration — so the 'AT' monogram is the mark.
  augmented: null,
  github: 'github',
  linear: 'linear',
  figma: 'figma',
  notion: 'notion',
  'google-docs': 'google-docs',
  'google-sheets': 'google-sheets',
  'google-slides': 'google-slides',
  // No `google-drive` row in the catalogue today (there is `onedrive`, which is
  // a different product). Monogram.
  'google-drive': null,
  // The gap ENG-9319 called out by name: Slack is a channel, not a catalogued
  // integration, so there is no row and no icon. Monogram.
  slack: null,
  youtube: 'youtube',
};

/**
 * The catalogue row for a provider, or null when we ship no logo for it.
 *
 * A function rather than direct map access so every caller shares one lookup —
 * the duplication that produced the ENG-9299 reconnect-card defect started as
 * "it is only an object index".
 */
export function catalogKeyForProvider(provider: ArtefactProvider): string | null {
  return ARTEFACT_PROVIDER_CATALOG_KEYS[provider];
}

/**
 * Raster extensions Slack's Block Kit `image` element will actually draw.
 *
 * NOT a general image-format list. `svg` is deliberately absent even though
 * every browser renders one, because Slack's image element does not — and every
 * logo in the catalogue is an SVG today (`logos.composio.dev` serves
 * `image/svg+xml`; GitHub's is a Wikimedia SVG). That single fact is why the
 * Slack renderer's layout is conditional rather than unconditional.
 */
const SLACK_RENDERABLE_IMAGE_EXTENSIONS: ReadonlySet<string> = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'bmp',
]);

/**
 * Can Slack's Block Kit `image` element draw this url?
 *
 * DEFAULTS TO FALSE, AND THE ASYMMETRY IS THE DESIGN. A wrong `true` puts a
 * broken-image placeholder in front of a user on a card that is reporting their
 * work finished; a wrong `false` merely keeps today's tidy single-section
 * layout, which nobody has complained about. So anything this cannot positively
 * identify as a raster image — an SVG, an extensionless CDN path, a shortener, a
 * presigned url with the extension in the query string, an unparseable string —
 * resolves to false and the caller keeps the existing layout.
 *
 * Reads the PATH only, never the query string, for the same reason
 * `deriveArtefactFormat` does: `?file=logo.png` says nothing about the body.
 * And it never fetches the url to read its Content-Type — `validateArtefactUrl`'s
 * docblock rules that out for kind-sniffing and the reason is identical here.
 */
export function canRenderImageInSlack(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  // Slack fetches the image itself, so it has to be publicly dereferenceable
  // over http(s). Anything else is not a url Slack can draw.
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
  const ext = /\.([a-z0-9]{1,5})$/.exec(parsed.pathname.toLowerCase())?.[1];
  if (ext === undefined) return false;
  return SLACK_RENDERABLE_IMAGE_EXTENSIONS.has(ext);
}
